/** Lightweight, interactive SVG chart components matching the dash.css design system. */
import { useRef, useState } from "react";

/** Compact number formatting: 1234 → 1.2k, 1_200_000 → 1.2M. */
function compact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return String(Math.round(n));
}

/** Evenly-spaced indices for axis ticks (so a 30-point series shows ~7 dates). */
function pickEvenlyIdx(n: number, k: number): number[] {
  if (n <= k) return Array.from({ length: n }, (_, i) => i);
  const step = (n - 1) / (k - 1);
  return Array.from({ length: k }, (_, i) => Math.round(i * step));
}

interface AreaChartProps {
  values: number[];
  /** Optional per-point labels (ideally same length as values) for hover/axis. */
  labels?: string[];
  height?: number;
}

export function AreaChart({ values, labels = [], height = 200 }: AreaChartProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [hi, setHi] = useState<number | null>(null);
  if (!values.length) return null;
  const w = 900;
  const h = height;
  const padL = 48;
  const padR = 12;
  const padT = 12;
  const padB = 32;
  const chartW = w - padL - padR;
  const chartH = h - padT - padB;

  const max = Math.max(...values, 1);
  const denom = values.length > 1 ? values.length - 1 : 1;

  const pts = values.map((v, i) => ({
    x: padL + (i / denom) * chartW,
    y: padT + chartH - (v / max) * chartH,
  }));

  const linePath = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const fillPath = `${linePath} L${pts[pts.length - 1]!.x.toFixed(1)},${(padT + chartH).toFixed(1)} L${pts[0]!.x.toFixed(1)},${(padT + chartH).toFixed(1)} Z`;

  const gridCount = 4;
  const gridLines = Array.from({ length: gridCount + 1 }, (_, i) => {
    const val = Math.round((max / gridCount) * i);
    return { y: padT + chartH - (val / max) * chartH, val };
  });
  const tickIdxs = pickEvenlyIdx(values.length, 7);

  function onMove(e: React.MouseEvent) {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xf = (e.clientX - rect.left) / rect.width;
    const cs = padL / w;
    const ce = (w - padR) / w;
    const t = Math.min(1, Math.max(0, (xf - cs) / (ce - cs)));
    setHi(Math.round(t * (values.length - 1)));
  }

  return (
    <div className="chart-wrap" ref={wrapRef} onMouseMove={onMove} onMouseLeave={() => setHi(null)}>
      <svg viewBox={`0 0 ${w} ${h}`} className="chart" preserveAspectRatio="none" style={{ height }}>
        <defs>
          <linearGradient id="mintFade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="oklch(82% 0.16 165)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="oklch(82% 0.16 165)" stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridLines.map(({ y, val }) => (
          <g key={val}>
            <line x1={padL} y1={y} x2={w - padR} y2={y} className="grid-line" />
            <text x={padL - 6} y={y + 3} textAnchor="end" className="axis-label">{compact(val)}</text>
          </g>
        ))}

        <path d={fillPath} className="area-fill" />
        <path d={linePath} className="area-line" />

        {hi != null && (
          <>
            <line x1={pts[hi]!.x} y1={padT} x2={pts[hi]!.x} y2={padT + chartH} className="guide-line" />
            <circle cx={pts[hi]!.x} cy={pts[hi]!.y} r="4" className="hover-dot" />
          </>
        )}

      </svg>

      {/* X-axis labels as HTML (the SVG uses preserveAspectRatio="none", which
          stretches/clips <text>); positioned by % so they always line up. */}
      {labels.length > 0 && (
        <div className="chart-xlabels" aria-hidden="true">
          {tickIdxs.map((idx) =>
            labels[idx] ? (
              <span
                key={idx}
                className="chart-xlabel"
                style={{
                  left: `${(pts[idx]!.x / w) * 100}%`,
                  transform:
                    idx === tickIdxs[0]
                      ? "translateX(0)"
                      : idx === tickIdxs[tickIdxs.length - 1]
                        ? "translateX(-100%)"
                        : "translateX(-50%)",
                }}
              >
                {labels[idx]}
              </span>
            ) : null,
          )}
        </div>
      )}

      {hi != null && (
        <div className="chart-tip" style={{ left: `${(pts[hi]!.x / w) * 100}%`, top: pts[hi]!.y }}>
          {labels[hi] && <span className="t-label">{labels[hi]}</span>}
          <span className="t-val">{values[hi]!.toLocaleString("en-US")}</span>
        </div>
      )}
    </div>
  );
}
